import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
} from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  AlertTriangle,
  CalendarRange,
  Clock3,
  Euro,
  Loader2,
  MapPin,
  Percent,
  RefreshCw,
} from 'lucide-react'
import { ApiError, fetchStores, type StoreOption } from '../api/auth'
import { fetchFourWallKpis, type FourWallResponse, type SalesSource } from '../api/fourwall'
import { DashboardGroupSubMenu } from '../components/DashboardGroupSubMenu'
import { ShopPills } from '../components/ShopPills'
import { PeriodControls } from '../components/PeriodControls'
import { useAuth } from '../context/AuthContext'
import { SHOP_REGIONS } from '../dashboard/regions'
import { canSelectShops, entitledStores } from '../lib/access'
import { formatCurrency, formatNumber, formatPercent } from '../lib/format'
import {
  formatRange,
  rangePresets,
  type DateRange,
  type RangePreset,
} from '../lib/ranges'

const PRESET_BASE =
  'rounded-lg border px-2.5 py-1.5 text-xs font-semibold transition-colors'
const PRESET_ACTIVE =
  'border-salsa-500 bg-salsa-500/10 text-salsa-700 dark:border-salsa-500/50 dark:text-salsa-300'
const PRESET_IDLE =
  'border-slate-200 text-slate-600 hover:border-salsa-300 hover:text-salsa-700 dark:border-white/10 dark:text-slate-300 dark:hover:border-salsa-500/40 dark:hover:text-salsa-200'

interface KpiDelta {
  text: string
  tone: 'good' | 'bad' | 'neutral'
  title?: string
}

const DELTA_TONE: Record<KpiDelta['tone'], string> = {
  good: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
  bad: 'bg-rose-500/15 text-rose-700 dark:text-rose-300',
  neutral: 'bg-slate-500/15 text-slate-600 dark:text-slate-300',
}

function signedHours(value: number): string {
  const sign = value < 0 ? '−' : value > 0 ? '+' : ''
  return `${sign}${formatNumber(Math.abs(value), 1)} h`
}

/** Inclusive day count for the selected ISO date range. */
function inclusiveDayCount(from: string, to: string): number {
  const start = Date.parse(`${from}T00:00:00Z`)
  const end = Date.parse(`${to}T00:00:00Z`)
  return Math.max(1, Math.round((end - start) / 86_400_000) + 1)
}

function KpiCard({
  label,
  value,
  secondary,
  hint,
  source,
  icon: Icon,
  accent,
  busy,
  delta,
}: {
  label: string
  value: string
  secondary?: string
  hint: string
  source: string
  icon: ComponentType<{ className?: string }>
  accent: string
  busy?: boolean
  delta?: KpiDelta
}) {
  return (
    <article className="card flex flex-col gap-1.5 p-3 sm:gap-2 sm:p-4" title={source} aria-busy={busy}>
      <div className="flex items-start justify-between gap-2">
        <span className="kpi-label">{label}</span>
        <span
          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg sm:h-8 sm:w-8 sm:rounded-xl ${accent}`}
          aria-hidden="true"
        >
          <Icon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
        </span>
      </div>
      <span className="flex items-center gap-2 text-lg font-semibold tabular-nums tracking-tight text-slate-900 sm:text-xl dark:text-white">
        {busy && (
          <Loader2
            className="h-3.5 w-3.5 shrink-0 animate-spin text-slate-400 dark:text-slate-500"
            aria-hidden="true"
          />
        )}
        {value}
      </span>
      {secondary && (
        <span className="text-sm font-semibold tabular-nums text-slate-500 dark:text-slate-400">
          {secondary}
        </span>
      )}
      {delta && (
        <span className={`badge w-fit ${DELTA_TONE[delta.tone]}`} title={delta.title}>
          {delta.text}
        </span>
      )}
      <span className="text-[11px] leading-snug text-slate-500 dark:text-slate-400">{hint}</span>
      {/* Provenance line for the desk; the phone card keeps to the number (D-106). */}
      <span className="mt-auto hidden text-[10px] leading-snug text-slate-400 sm:block dark:text-slate-500">
        {source}
      </span>
    </article>
  )
}

/** Which system answered the sales figure, said in the card's provenance line. */
const SALES_SOURCE_NOTE: Record<SalesSource, string> = {
  vmhub_history: 'VM Hub history archive · net sales, ex-VAT',
  mixed: 'VM Hub archive + Butlaroo Insights · summed per shop, per day',
  butlaroo: 'Butlaroo Insights · vendor aggregate, ex-VAT',
}

export function FourWallKpis({
  data,
  busy,
}: {
  data?: FourWallResponse
  busy: boolean
}) {
  const totals = data?.totals
  const gridDelta: KpiDelta | undefined =
    totals?.grid_diff != null
      ? {
          text: signedHours(totals.grid_diff),
          tone:
            totals.grid_diff < 0
              ? 'good'
              : totals.grid_diff > 0
                ? 'bad'
                : 'neutral',
          title:
            'Worked minus grid. Green is below grid; red is above grid. ' +
            'Today’s worked hours are elapsed roster time.',
        }
      : undefined

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2 sm:gap-3 xl:grid-cols-4">
      <KpiCard
        label="Sales"
        value={totals?.sales_eur != null ? formatCurrency(totals.sales_eur) : '—'}
        hint={
          data
            ? `${data.totals.stores_with_sales} of ${data.stores_requested} shops with sales`
            : 'Butlaroo Insights'
        }
        source={SALES_SOURCE_NOTE[data?.sales_source ?? 'butlaroo']}
        icon={Euro}
        accent="bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
        busy={busy}
      />
      <KpiCard
        label="COL% / COL"
        value={totals?.col_pct != null ? formatPercent(totals.col_pct, 1) : '—'}
        secondary={totals?.col_eur != null ? formatCurrency(totals.col_eur) : '—'}
        hint={
          data
            ? `${data.totals.stores_with_col} shop${data.totals.stores_with_col === 1 ? '' : 's'} with sales + cost`
            : 'Shiftbase · timesheets + roster today'
        }
        source={`Cost of labour ÷ sales · Shiftbase + ${
          data?.sales_source === 'vmhub_history'
            ? 'VM Hub archive'
            : data?.sales_source === 'mixed'
              ? 'archive + Butlaroo'
              : 'Butlaroo'
        }`}
        icon={Percent}
        accent="bg-sky-500/15 text-sky-600 dark:text-sky-400"
        busy={busy}
      />
      <KpiCard
        label="Hours used"
        value={
          totals?.worked_hours != null
            ? `${formatNumber(totals.worked_hours, 1)} h`
            : '—'
        }
        hint={
          data
            ? `${data.totals.stores_with_labour} of ${data.stores_requested} shops · actual`
            : 'Shiftbase · timesheets + roster today'
        }
        source="Shiftbase · worked hours (timesheets + roster today)"
        icon={Clock3}
        accent="bg-amber-500/15 text-amber-600 dark:text-amber-400"
        busy={busy}
      />
      <KpiCard
        label="Grid"
        value={
          totals?.grid_hours != null
            ? `${formatNumber(totals.grid_hours, 1)} h`
            : '—'
        }
        hint={
          data
            ? `Grid for period · ${data.stores.filter((row) => row.grid_hours != null).length} shops`
            : 'Labor grid'
        }
        source="COL labour grid · worked − grid difference"
        icon={CalendarRange}
        accent="bg-slate-500/15 text-slate-600 dark:text-slate-400"
        busy={busy}
        delta={gridDelta}
      />
      </div>
    </div>
  )
}

/**
 * 4-Wall Analysis (D-48/D-49/D-70/D-86/D-101). The same shop/period controls as
 * the General Overview drive the four headline KPIs. Multi-shop accounts wait for
 * an explicit shop/region choice; an account with exactly one shop opens on it
 * automatically because there is nothing to choose. There is no fallback data on
 * this screen: if a feed is missing, the card reports unavailable.
 */
export function FourWallPage() {
  const { user } = useAuth()
  const canPick = canSelectShops(user)
  // District managers only need All/None; other regional shortcuts are out of
  // their scope and would otherwise render disabled.
  const showRegionQuickSelect = canPick && user?.role !== 'district_manager'

  const now = useMemo(() => new Date(), [])
  const presets = useMemo<RangePreset[]>(
    () => rangePresets(now).filter((preset) => preset.label !== 'All time'),
    [now],
  )
  const [range, setRange] = useState<DateRange>(() => {
    const week = rangePresets(new Date()).find((preset) => preset.label === 'This week')
    return week
      ? { from: week.from, to: week.to, preset: 'This week' }
      : { from: presets[0].from, to: presets[0].to }
  })
  const [pickedIds, setPickedIds] = useState<number[]>([])
  const [stores, setStores] = useState<StoreOption[]>([])
  const [loadError, setLoadError] = useState<string | null>(null)
  const autoSelectedRef = useRef(false)

  useEffect(() => {
    if (!user) return
    let cancelled = false
    fetchStores()
      .then((list) => {
        if (!cancelled) setStores(list)
      })
      .catch(() => {
        if (!cancelled) setLoadError('Could not load the shop list.')
      })
    return () => {
      cancelled = true
    }
  }, [user])

  const visibleStores = useMemo<StoreOption[]>(
    () => entitledStores(user, stores),
    [stores, user],
  )
  const visibleIds = useMemo(() => visibleStores.map((store) => store.id), [visibleStores])

  // A one-shop account has nothing to choose, so open on its only shop instead
  // of asking for a pointless click (D-101). Multi-shop accounts keep the
  // explicit no-selection default, and a later "None" click is not overridden.
  useEffect(() => {
    if (autoSelectedRef.current || visibleIds.length !== 1) return
    autoSelectedRef.current = true
    setPickedIds([visibleIds[0]])
  }, [visibleIds])

  const regionOptions = useMemo(
    () =>
      SHOP_REGIONS.map((region) => {
        const codes = new Set(region.storeCodes)
        return {
          ...region,
          ids: visibleStores
            .filter((store) => codes.has(store.code))
            .map((store) => store.id),
        }
      }),
    [visibleStores],
  )

  const selectedIds = useMemo(() => {
    const base = pickedIds
    const allowed = new Set(visibleIds)
    return base.filter((id) => allowed.has(id))
  }, [pickedIds, visibleIds])
  const allSelected = visibleIds.length > 0 && selectedIds.length === visibleIds.length
  const noneSelected = selectedIds.length === 0

  const selected = selectedIds
  const selectedSummary =
    selected.length === 0
      ? 'no shop selected'
      : selected.length === 1
        ? (visibleStores.find((store) => store.id === selected[0])?.label ?? '1 shop')
        : `${selected.length} shops`

  const kpiQuery = useQuery({
    queryKey: ['fourwall', range.from, range.to, selected.join(',')],
    queryFn: ({ signal }) =>
      fetchFourWallKpis({ from: range.from, to: range.to, storeIds: selected, signal }),
    enabled: Boolean(user) && selected.length > 0,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 1,
  })
  const kpiData = kpiQuery.data

  const toggleStore = useCallback((id: number) => {
    setPickedIds((current) =>
      current.includes(id) ? current.filter((value) => value !== id) : [...current, id],
    )
  }, [])
  const selectAll = useCallback(() => setPickedIds(visibleIds), [visibleIds])
  const clearAll = useCallback(() => setPickedIds([]), [])
  const refreshNow = () => {
    void kpiQuery.refetch()
  }

  const days = inclusiveDayCount(range.from, range.to)
  const busy = kpiQuery.isPending && selected.length > 0
  const kpiError =
    kpiQuery.error instanceof ApiError
      ? kpiQuery.error.message
      : kpiQuery.error
        ? '4-Wall figures are unavailable right now.'
        : null

  return (
    <div id="four-wall-panel" className="space-y-5">
      <DashboardGroupSubMenu
        activeKey="four_wall"
        fallbackGroupKey="insights"
        panelId="four-wall-panel"
      />

      <section className="card space-y-4 p-4">
        <div className="flex flex-wrap items-end justify-between gap-2">
          <div>
            <h2 className="text-sm font-semibold text-slate-900 dark:text-white">
              Four walls, one period
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {formatRange(range)} · {days} days · {selectedSummary}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={refreshNow}
              disabled={!user || selected.length === 0 || kpiQuery.isFetching}
              title="Refresh the 4-Wall figures"
              aria-label="Refresh 4-wall KPIs"
              className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-2.5 py-1.5 text-[11px] font-semibold text-slate-600 transition-colors hover:border-slate-300 disabled:cursor-not-allowed disabled:opacity-40 dark:border-white/10 dark:text-slate-300 dark:hover:border-white/20"
            >
              <RefreshCw
                className={`h-3.5 w-3.5 ${kpiQuery.isFetching ? 'animate-spin' : ''}`}
                aria-hidden="true"
              />
              Refresh
            </button>
          </div>
        </div>

        <PeriodControls value={range} onChange={setRange} presets={presets} now={now} />

        {canPick && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
              Region
            </span>
            <button
              type="button"
              onClick={selectAll}
              disabled={visibleIds.length === 0}
              aria-pressed={allSelected}
              className={`${PRESET_BASE} ${allSelected ? PRESET_ACTIVE : PRESET_IDLE} disabled:cursor-not-allowed disabled:opacity-40`}
            >
              All
            </button>
            <button
              type="button"
              onClick={clearAll}
              disabled={noneSelected}
              aria-pressed={noneSelected}
              className={`${PRESET_BASE} ${noneSelected ? PRESET_ACTIVE : PRESET_IDLE} disabled:cursor-not-allowed disabled:opacity-40`}
            >
              None
            </button>
            {showRegionQuickSelect &&
              regionOptions.map((region) => {
                const active =
                  region.ids.length > 0 &&
                  region.ids.length === selectedIds.length &&
                  region.ids.every((id) => selectedIds.includes(id))
                return (
                  <button
                    key={region.key}
                    type="button"
                    onClick={() => setPickedIds(region.ids)}
                    disabled={region.ids.length === 0}
                    aria-pressed={active}
                    title={`${region.name} · ${region.ids.length} shops (${region.storeCodes.join(', ')})`}
                    className={`${PRESET_BASE} ${active ? PRESET_ACTIVE : PRESET_IDLE} disabled:cursor-not-allowed disabled:opacity-40`}
                  >
                    {region.name}
                  </button>
                )
              })}
          </div>
        )}

        {canPick ? (
          <ShopPills
            stores={visibleStores}
            selectedIds={selectedIds}
            onToggle={toggleStore}
            onSelectAll={selectAll}
            onClear={clearAll}
            showControls={false}
          />
        ) : selected.length === 0 && visibleStores[0] ? (
          <button
            type="button"
            onClick={() => setPickedIds([visibleStores[0].id])}
            className={`${PRESET_BASE} ${PRESET_IDLE} inline-flex items-center gap-1`}
          >
            <MapPin className="h-3 w-3" aria-hidden="true" />
            Use {visibleStores[0].label}
          </button>
        ) : (
          <p className="flex items-center gap-1 text-[11px] text-slate-500 dark:text-slate-400">
            <MapPin className="h-3 w-3" aria-hidden="true" />
            {visibleStores[0]?.label ?? 'Your store'}
          </p>
        )}

        {loadError && (
          <p className="flex items-center gap-1 text-[11px] text-amber-700 dark:text-amber-300">
            <MapPin className="h-3 w-3" aria-hidden="true" /> {loadError}
          </p>
        )}
      </section>

      {selected.length === 0 ? (
        <section className="card space-y-3 p-6 text-center">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
            Choose at least one shop
          </h3>
          <p className="mx-auto max-w-md text-xs text-slate-500 dark:text-slate-400">
            Pick at least one shop above to bring the 4-Wall figures back.
          </p>
          {canPick ? (
            <button
              type="button"
              onClick={selectAll}
              className="inline-flex items-center gap-1.5 rounded-xl bg-salsa-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-salsa-600"
            >
              Select all {visibleStores.length} shops
            </button>
          ) : null}
        </section>
      ) : (
        <>
          <FourWallKpis data={kpiData} busy={busy} />

          {kpiData && kpiData.warnings.length > 0 && (
            <ul className="space-y-1 rounded-2xl border border-slate-200 bg-slate-50/70 px-4 py-3 text-[11px] leading-relaxed text-slate-600 dark:border-white/10 dark:bg-white/5 dark:text-slate-300">
              {kpiData.warnings.map((warning) => (
                <li key={warning} className="flex items-start gap-2">
                  <AlertTriangle
                    className="mt-0.5 h-3 w-3 shrink-0 text-slate-400 dark:text-slate-500"
                    aria-hidden="true"
                  />
                  <span>{warning}</span>
                </li>
              ))}
            </ul>
          )}

          {kpiError && (
            <div className="flex items-start gap-2 rounded-2xl border border-amber-200 bg-amber-50/70 px-4 py-3 text-xs text-amber-900 dark:border-amber-500/25 dark:bg-amber-500/10 dark:text-amber-200">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span>
                <strong>4-Wall figures are unavailable.</strong> {kpiError}
              </span>
              <button
                type="button"
                onClick={() => void kpiQuery.refetch()}
                className="ml-auto inline-flex items-center gap-1 rounded-lg px-2 py-1 font-semibold hover:bg-amber-500/10"
              >
                <RefreshCw
                  className={`h-3.5 w-3.5 ${kpiQuery.isFetching ? 'animate-spin' : ''}`}
                  aria-hidden="true"
                />
                Retry
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
